import axios, { AxiosResponse, Method } from "axios"

import { API_ERROR_HANDRING } from "@/common/eventdefs"
import { Const, ErrorCode } from "@/common/globals"
import { getLoginInfo } from "@/infrastructures"
import { AbstractRequest } from "@/oplux/v3/api/common/AbstractRequest"
import { AbstractResponse } from "@/oplux/v3/api/common/AbstractResponse"
import { ApiResult } from "@/oplux/v3/api/common/ApiResult"
import { CommonRequest } from "@/oplux/v3/api/common/valueobject/CommonRequest"
import { CommonResponse } from "@/oplux/v3/api/common/valueobject/CommonResponse"
import { ErrorItem } from "@/oplux/v3/api/common/valueobject/ErrorItem"
import { MessageItem } from "@/oplux/v3/api/common/valueobject/MessageItem"
import { ScreenApiRequestHeader } from "@/oplux/v3/api/common/valueobject/ScreenApiRequestHeader"
import { commonOverlayModule } from "@/stores/modules/CommonOverlayModule"
import { SnackbarUtils } from "@/utils/SnackbarUtils"

import { vueInstance } from "%%/main"

type ApiClientOptions = {
  /** ログイン情報から加盟店ID を取得できないときに設定する加盟店ID */
  overrideShopId?: string
  /** 認証エラーをハンドリングするかの設定 */
  handlingAuthorizeError: boolean
  /** メッセージがあった場合に表示するかの設定 */
  showMessages: boolean
  /** エラーメッセージがあった場合に表示するかの設定 */
  showErrorMessages: boolean
  /** リクエスト中にオーバーレイを表示するかの設定 */
  showOverlay: boolean
}

/**
 * レガシー画面API（blanco が生成した API と同じ認証方法の API）の呼び出しに使う
 */
export class ApiClient {
  /**
   * API呼び出しのEntryポイント
   */
  async send<T extends AbstractResponse>(
    request: AbstractRequest,
    {
      handlingAuthorizeError = true,
      showMessages = true,
      showErrorMessages = true,
      showOverlay = false,
      ...options
    }: Partial<ApiClientOptions> = {}
  ): Promise<ApiResult<T>> {
    request.trimString()
    const url = this.makeUrl(request)
    const body = await this.makeBody(request, options.overrideShopId)
    let commonResponse: CommonResponse<T> | undefined
    const errors = new Array<ErrorItem>()
    const messages = new Array<MessageItem>()

    if (showOverlay) {
      commonOverlayModule.changeOverlay(true)
    }
    if (!process.env.VUE_APP_API_ENDPOINT) {
      /* JSON ファイルを取得する */
      const res = await this.callAxios<T>(
        this.getJsonFileName(request),
        "GET", // 静的ファイルを取得するため GET メソッド固定
        body
      )
      commonResponse = res.data
    } else {
      const method = request.requestMetaInfo().method
      const res = await this.callAxios<T>(url, method, body)
      commonResponse = res.data
    }
    if (showOverlay) {
      commonOverlayModule.changeOverlay(false)
    }

    if (commonResponse) {
      if (commonResponse.messages && commonResponse.messages.length !== 0) {
        commonResponse.messages.forEach(message => messages.push(message))
      }
      if (commonResponse.errors && commonResponse.errors.length !== 0) {
        commonResponse.errors.forEach(item => {
          errors.push(item)
        })
      }
    }

    if (
      handlingAuthorizeError &&
      errors.filter(
        e => e.code === ErrorCode.AuthorizeErrorInvalidToken || e.code === ErrorCode.AuthorizeErrorSourceIpNotAllowed
      ).length > 0
    ) {
      // 認証エラー時の処理は Communicator の既存処理を使う
      vueInstance.$emit(API_ERROR_HANDRING, errors)
      return new ApiResult<T>(commonResponse, messages, errors)
    }

    if (showMessages && messages.length > 0) {
      SnackbarUtils.showMessages(messages)
    }
    if (showErrorMessages && errors.length > 0) {
      SnackbarUtils.showErrors(errors)
    }

    return new ApiResult<T>(commonResponse, messages, errors)
  }

  /**
   * デバグ用JSONの読込ルーチン。
   * JSON ファイルは
   * public/json/api/電文処理ID/電文ID.json
   * とし、1 API につき固定の 1 JSON を返す。
   */
  private getJsonFileName(request: AbstractRequest): string {
    const requestObjClassName = request.telegramId()
    // 接尾辞「Request」の存在を確定させて、電文処理IDを作る
    let processName: string
    const length = requestObjClassName.length
    if (requestObjClassName.endsWith(Const.PostRequestSuffix)) {
      processName = requestObjClassName.substring(0, length - Const.PostRequestSuffix.length)
    } else if (requestObjClassName.endsWith(Const.PutRequestSuffix)) {
      processName = requestObjClassName.substring(0, length - Const.PutRequestSuffix.length)
    } else {
      throw new Error("Unexpected value. " + requestObjClassName)
    }
    const method = request.requestMetaInfo().method
    return "/jsons/api/" + processName + "/" + method + "CommonResponse.json"
  }

  private makeUrl(request: AbstractRequest) {
    return process.env.VUE_APP_API_ENDPOINT + request.requestMetaInfo().uri
  }

  private async makeBody(request: AbstractRequest, overrideShopId?: string) {
    const _request = await this.makeCommonRequest(request, overrideShopId)

    return JSON.stringify(_request)
  }
  /**
   * 電文をAPIのエンベロープ（CommonRequest）でくるみます。
   * header 情報（token の設定など）もここで行います。
   */
  private async makeCommonRequest(request: AbstractRequest, overrideShopId?: string): Promise<CommonRequest> {
    const info = await this.prepareScreenApiRequestHeader(request, overrideShopId)

    const commonRequest = new CommonRequest()
    commonRequest.info = info
    commonRequest.telegram = request

    return commonRequest
  }

  private async prepareScreenApiRequestHeader(
    request: AbstractRequest,
    overrideShopId?: string
  ): Promise<ScreenApiRequestHeader> {
    const header = new ScreenApiRequestHeader()

    // 認証が必要なAPIの場合はトークンの取得が先
    if (request.requestMetaInfo().isAuthenticationRequire) {
      // router でログインチェックしてるので null にならない
      const loginInfo = getLoginInfo()!
      header.token = loginInfo.loginToken
      header.shopId = loginInfo.loginOperator ? loginInfo.loginOperator.companyId : ""
    }
    if (overrideShopId) {
      header.shopId = overrideShopId
    }

    return header
  }

  private async callAxios<T extends AbstractResponse>(
    url: string,
    method: string,
    body: string
  ): Promise<AxiosResponse<CommonResponse<T>>> {
    try {
      return await axios.request<CommonResponse<T>>({
        url,
        method: method as Method,
        headers: {
          "content-type": "application/json",
        },
        data: body,
      })
    } catch (exception) {
      if (axios.isAxiosError(exception) && exception.response) {
        return exception.response
      }
      throw exception
    }
  }
}
